import React, { useCallback, useEffect, useMemo } from 'react';
import { useAnalyticsEvents as useAnalyticsEventsNext } from '@atlaskit/analytics-next';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import { useAnalyticsEvents } from '../../common/analytics/generated/use-analytics-events';
import { CardDisplay } from '../../constants';
import { useSmartLink } from '../../state';
import { succeedUfoExperience } from '../../state/analytics';
import { getClickUrl, getDefinitionId, getExtensionKey, getFirstPartyIdentifier, getObjectAri, getObjectIconUrl, getObjectName, getResourceType, getServices, getThirdPartyARI, isFinalState } from '../../state/helpers';
import { SmartLinkModalProvider } from '../../state/modal';
import { isSpecialClick, isSpecialEvent, isSpecialKey } from '../../utils';
import { combineActionOptions } from '../../utils/actions/combine-action-options';
import { fireLinkClickedEvent } from '../../utils/analytics/click';
import { SmartLinkAnalyticsContext } from '../../utils/analytics/SmartLinkAnalyticsContext';
import { isFlexibleUiCard } from '../../utils/flexible';
import * as measure from '../../utils/performance';
import { BlockCard } from '../BlockCard';
import { EmbedCard } from '../EmbedCard';
import FlexibleCard from '../FlexibleCard';
import { InlineCard } from '../InlineCard';
import { useFire3PWorkflowsClickEvent } from '../SmartLinkEvents/useSmartLinkEvents';
var thirdPartyARIPrefix = 'ari:third-party';
function Component(_ref) {
  var id = _ref.id,
    url = _ref.url,
    isSelected = _ref.isSelected,
    isHovered = _ref.isHovered,
    frameStyle = _ref.frameStyle,
    platform = _ref.platform,
    onClick = _ref.onClick,
    appearance = _ref.appearance,
    onResolve = _ref.onResolve,
    onError = _ref.onError,
    testId = _ref.testId,
    actionOptionsProp = _ref.actionOptions,
    inheritDimensions = _ref.inheritDimensions,
    embedIframeRef = _ref.embedIframeRef,
    embedIframeUrlType = _ref.embedIframeUrlType,
    inlinePreloaderStyle = _ref.inlinePreloaderStyle,
    ui = _ref.ui,
    children = _ref.children,
    showHoverPreview = _ref.showHoverPreview,
    hoverPreviewOptions = _ref.hoverPreviewOptions,
    removeTextHighlightingFromTitle = _ref.removeTextHighlightingFromTitle,
    resolvingPlaceholder = _ref.resolvingPlaceholder,
    truncateInline = _ref.truncateInline,
    CompetitorPrompt = _ref.CompetitorPrompt,
    hideIconLoadingSkeleton = _ref.hideIconLoadingSkeleton,
    disablePreviewPanel = _ref.disablePreviewPanel;
  var _useAnalyticsEventsNe = useAnalyticsEventsNext(),
    createAnalyticsEvent = _useAnalyticsEventsNe.createAnalyticsEvent;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    fireEvent = _useAnalyticsEvents.fireEvent;

  // Get state, actions for this card.
  var _useSmartLink = useSmartLink(id, url),
    state = _useSmartLink.state,
    actions = _useSmartLink.actions,
    config = _useSmartLink.config,
    renderers = _useSmartLink.renderers,
    error = _useSmartLink.error,
    isPreviewPanelAvailable = _useSmartLink.isPreviewPanelAvailable,
    openPreviewPanel = _useSmartLink.openPreviewPanel;
  var ari = getObjectAri(state.details);
  var name = getObjectName(state.details);
  var definitionId = getDefinitionId(state.details);
  var extensionKey = getExtensionKey(state.details);
  var resourceType = getResourceType(state.details);
  var services = getServices(state.details);
  var thirdPartyARI = getThirdPartyARI(state.details);
  var firstPartyIdentifier = getFirstPartyIdentifier();
  var isFlexibleUi = useMemo(function () {
    return isFlexibleUiCard(children, ui);
  }, [children, ui]);
  var actionOptions = combineActionOptions({
    actionOptions: actionOptionsProp,
    platform: platform
  });
  var fire3PClickEvent = useFire3PWorkflowsClickEvent(firstPartyIdentifier, thirdPartyARI);

  // Setup UI handlers.
  var handleClickWrapper = useCallback(function (event) {
    var isModifierKeyPressed = isSpecialKey(event) || isSpecialClick(event);
    fireEvent('ui.smartLink.clicked', {
      id: id,
      display: isFlexibleUi ? CardDisplay.Flexible : appearance,
      definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null,
      isModifierKeyPressed: isModifierKeyPressed
    });
    if (fg('platform_smartlink_3pclick_analytics')) {
      if (thirdPartyARI && thirdPartyARI.startsWith(thirdPartyARIPrefix)) {
        var clickURL = getClickUrl(url, state.details);
        if (clickURL === url && event.button === 0) {
          // We want to verify that left click only triggers this
          // event being fired, not right click, so we check button
          // === 0, which indicates left click as defined here:
          // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
          // For questions or concerns about this event,
          // please reach out to the 3P Workflows Team via Slack in #help-3p-connector-workflow
          fire3PClickEvent();
        }
      }
    }
    var isDisablePreviewPanel = disablePreviewPanel && editorExperiment('platform_editor_preview_panel_linking_exp', true, {
      exposure: true
    });

    // If preview panel is available and the user clicked on the link,
    // delegate the click to the preview panel handler
    if (!isModifierKeyPressed && ari && name && openPreviewPanel && isPreviewPanelAvailable !== null && isPreviewPanelAvailable !== void 0 && isPreviewPanelAvailable({
      ari: ari
    }) && !isDisablePreviewPanel) {
      event.preventDefault();
      event.stopPropagation();
      openPreviewPanel({
        url: url,
        ari: ari,
        name: name,
        iconUrl: getObjectIconUrl(state.details)
      });
      fireLinkClickedEvent(createAnalyticsEvent)(event, {
        attributes: {
          clickOutcome: 'previewPanel'
        }
      });
      return;
    } else if (!onClick && !isFlexibleUi) {
      var clickUrl = getClickUrl(url, state.details);

      // Ctrl+left click on mac typically doesn't trigger onClick
      // The event could have potentially had `e.preventDefault()` called on it by now
      // event by smart card internally
      // If it has been called then only then can `isSpecialEvent` be true.
      var target = isSpecialEvent(event) ? '_blank' : '_self';
      window.open(clickUrl, target);
      fireLinkClickedEvent(createAnalyticsEvent)(event, {
        attributes: {
          clickOutcome: target === '_blank' ? 'clickThroughNewTabOrWindow' : 'clickThrough'
        }
      });
    } else {
      if (onClick) {
        onClick(event);
      }
      fireLinkClickedEvent(createAnalyticsEvent)(event);
    }
  }, [fireEvent, fire3PClickEvent, id, isFlexibleUi, appearance, definitionId, onClick, url, state.details, ari, name, isPreviewPanelAvailable, openPreviewPanel, createAnalyticsEvent, disablePreviewPanel, thirdPartyARI]);
  var handleAuthorize = useCallback(function () {
    return actions.authorize(appearance);
  }, [actions, appearance]);
  var handleRetry = useCallback(function () {
    actions.reload();
  }, [actions]);
  var handleInvoke = useCallback(function (opts) {
    return actions.invoke(opts, appearance);
  }, [actions, appearance]);

  // NB: for each status change in a Smart Link, a performance mark is created.
  // Measures are sent relative to the first mark, matching what a user sees.
  useEffect(function () {
    measure.mark(id, state.status);
    if (state.status !== 'pending' && state.status !== 'resolving') {
      var _state$error, _state$error2;
      measure.create(id, state.status);
      if (state.status === 'resolved') {
        var _measure$getMeasure$d, _measure$getMeasure;
        fireEvent('operational.smartLink.resolved', {
          definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null,
          duration: (_measure$getMeasure$d = (_measure$getMeasure = measure.getMeasure(id, state.status)) === null || _measure$getMeasure === void 0 ? void 0 : _measure$getMeasure.duration) !== null && _measure$getMeasure$d !== void 0 ? _measure$getMeasure$d : null
        });
      } else if (((_state$error = state.error) === null || _state$error === void 0 ? void 0 : _state$error.type) !== 'ResolveUnsupportedError' && ((_state$error2 = state.error) === null || _state$error2 === void 0 ? void 0 : _state$error2.type) !== 'UnsupportedError') {
        fireEvent('operational.smartLink.unresolved', {
          definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null,
          reason: state.status,
          error: state.error === undefined ? null : {
            name: state.error.name,
            kind: state.error.kind,
            type: state.error.type
          }
        });
      }
    }
  }, [id, appearance, state.status, state.error, definitionId, extensionKey, resourceType, fireEvent]);

  // NB: once the smart-card has rendered into an end state, we capture
  // this as a successful render. These can be one of:
  // - the resolved state: when metadata is shown;
  // - the unresolved states: viz. forbidden, not_found, unauthorized, errored.
  useEffect(function () {
    if (isFinalState(state.status)) {
      succeedUfoExperience('smart-link-rendered', id || 'NULL', {
        extensionKey: extensionKey,
        display: isFlexibleUi ? 'flexible' : appearance
      });

      // UFO will disregard this if authentication experience has not yet been started
      succeedUfoExperience('smart-link-authenticated', id || 'NULL', {
        display: isFlexibleUi ? 'flexible' : appearance
      });
      fireEvent('ui.smartLink.renderSuccess', {
        display: isFlexibleUi ? 'flexible' : appearance
      });
    }
  }, [appearance, extensionKey, fireEvent, id, isFlexibleUi, state.status]);
  var onIframeDwell = useCallback(function (dwellTime, dwellPercentVisible) {
    fireEvent('ui.smartLinkIframe.dwelled', {
      id: id,
      definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null,
      display: isFlexibleUi ? 'flexible' : appearance,
      dwellPercentVisible: dwellPercentVisible,
      dwellTime: dwellTime
    });
  }, [id, appearance, definitionId, isFlexibleUi, fireEvent]);
  var onIframeFocus = useCallback(function () {
    fireEvent('ui.smartLinkIframe.focused', {
      id: id,
      definitionId: definitionId !== null && definitionId !== void 0 ? definitionId : null,
      display: isFlexibleUi ? 'flexible' : appearance
    });
  }, [id, appearance, definitionId, isFlexibleUi, fireEvent]);
  if (isFlexibleUi) {
    var cardState = state;
    if (error) {
      if ((error === null || error === void 0 ? void 0 : error.name) === 'APIError') {
        cardState = {
          status: 'errored'
        };
      } else {
        throw error;
      }
    }
    return /*#__PURE__*/React.createElement(FlexibleCard, {
      id: id,
      cardState: cardState,
      onAuthorize: services.length && handleAuthorize || undefined,
      onClick: handleClickWrapper,
      origin: "smartLinkCard",
      renderers: renderers,
      ui: ui,
      showHoverPreview: showHoverPreview,
      hoverPreviewOptions: hoverPreviewOptions,
      actionOptions: actionOptions,
      url: url,
      testId: testId,
      onResolve: onResolve,
      onError: onError
    }, children);
  }

  // We have to keep this last to prevent hook order from being violated
  if (error) {
    throw error;
  }
  switch (appearance) {
    case 'inline':
      return /*#__PURE__*/React.createElement(InlineCard, {
        id: id,
        url: url,
        renderers: renderers,
        cardState: state,
        handleAuthorize: services.length && handleAuthorize || undefined,
        handleFrameClick: handleClickWrapper,
        isSelected: isSelected,
        isHovered: isHovered,
        onResolve: onResolve,
        onError: onError,
        testId: testId,
        inlinePreloaderStyle: inlinePreloaderStyle,
        showHoverPreview: showHoverPreview,
        hoverPreviewOptions: hoverPreviewOptions,
        actionOptions: actionOptions,
        removeTextHighlightingFromTitle: removeTextHighlightingFromTitle,
        resolvingPlaceholder: resolvingPlaceholder,
        truncateInline: truncateInline,
        hideIconLoadingSkeleton: hideIconLoadingSkeleton
      });
    case 'block':
      return /*#__PURE__*/React.createElement(BlockCard, {
        id: id,
        url: url,
        renderers: renderers,
        authFlow: config && config.authFlow,
        cardState: state,
        handleAuthorize: services.length && handleAuthorize || undefined,
        handleFrameClick: handleClickWrapper,
        isSelected: isSelected,
        onResolve: onResolve,
        onError: onError,
        testId: testId,
        actionOptions: actionOptions,
        CompetitorPrompt: CompetitorPrompt,
        hideIconLoadingSkeleton: hideIconLoadingSkeleton
      });
    case 'embed':
      return /*#__PURE__*/React.createElement(EmbedCard, {
        id: id,
        url: url,
        renderers: renderers,
        cardState: state,
        iframeUrlType: embedIframeUrlType,
        handleAuthorize: services.length && handleAuthorize || undefined,
        handleErrorRetry: handleRetry,
        handleFrameClick: handleClickWrapper,
        handleInvoke: handleInvoke,
        isSelected: isSelected,
        frameStyle: frameStyle,
        platform: platform,
        onResolve: onResolve,
        onError: onError,
        testId: testId,
        inheritDimensions: inheritDimensions,
        actionOptions: actionOptions,
        ref: embedIframeRef,
        onIframeDwell: onIframeDwell,
        onIframeFocus: onIframeFocus,
        CompetitorPrompt: CompetitorPrompt,
        hideIconLoadingSkeleton: hideIconLoadingSkeleton
      });
  }
}
export var CardWithUrlContent = function CardWithUrlContent(props) {
  var display = isFlexibleUiCard(props.children, props === null || props === void 0 ? void 0 : props.ui) ? CardDisplay.Flexible : props.appearance;
  return /*#__PURE__*/React.createElement(SmartLinkModalProvider, null, /*#__PURE__*/React.createElement(SmartLinkAnalyticsContext, {
    url: props.url,
    id: props.id,
    display: display
  }, /*#__PURE__*/React.createElement(Component, props)));
};